// Pure helpers for the in-cleanup inline record editor (Donna's
// request: fix a name / add a missing email / correct an address
// WITHOUT leaving the Cleanup workspace to hunt for "Edit details" on
// the full record).
//
// Root-safe by construction — this module imports nothing but its own
// local input shapes, so its diff logic is unit-testable in the node
// pool exactly like decide-rail-applicability.ts / cleanup-records.ts.
//
// The editable set is deliberately NARROW: the core fields a front-desk
// steward actually corrects. Sensitive fields (SSN / DL / bank) are
// excluded by construction — they never appear in a draft, so the
// inline editor can never expose or write them (SENSITIVE_FIELD rule:
// decryption + sensitive writes have their own dedicated flow). This
// mirrors the same field subset the ContactPage edit form allows, just
// scoped to the handful worth fixing in-flow.

// The contact core fields, in display order. Legal name + preferred
// name + primary email + primary phone + mailing address.
export const CONTACT_CORE_FIELDS = [
  "legal_first_name",
  "legal_last_name",
  "preferred_name",
  "email",
  "phone_primary",
  "street_line_1",
  "street_line_2",
  "city",
  "state",
  "zip",
] as const;

export type ContactCoreField = (typeof CONTACT_CORE_FIELDS)[number];

// The household-level mailing address: street lines (migration 0181)
// + city / state / zip. The physical_* address is deliberately NOT in
// the inline editor — the cleanup drawer stays narrow; a separate
// physical address is edited from the household page's address card.
export const HOUSEHOLD_ADDRESS_FIELDS = [
  "primary_street_line_1",
  "primary_street_line_2",
  "primary_city",
  "primary_state",
  "primary_zip",
] as const;

export type HouseholdAddressField = (typeof HOUSEHOLD_ADDRESS_FIELDS)[number];

// A draft holds STRINGS for controlled inputs (null → "" on the way in,
// "" → null on the way out). Keyed by the exact PATCH field names.
export type ContactCoreDraft = Record<ContactCoreField, string>;
export type HouseholdAddressDraft = Record<HouseholdAddressField, string>;

// Narrow input shapes so this module has zero imports (root-safe). Any
// SafeContact / Household satisfies these structurally.
type ContactSource = { [K in ContactCoreField]?: string | null };
type HouseholdSource = { [K in HouseholdAddressField]?: string | null };

export function contactDraftFrom(c: ContactSource): ContactCoreDraft {
  const draft = {} as ContactCoreDraft;
  for (const key of CONTACT_CORE_FIELDS) {
    draft[key] = c[key] ?? "";
  }
  return draft;
}

export function householdDraftFrom(h: HouseholdSource): HouseholdAddressDraft {
  const draft = {} as HouseholdAddressDraft;
  for (const key of HOUSEHOLD_ADDRESS_FIELDS) {
    draft[key] = h[key] ?? "";
  }
  return draft;
}

// Treat empty / whitespace-only as null so re-typing the same value into
// a blank field (or trimming trailing spaces) doesn't produce a no-op
// audit row. Matches the ContactPage edit form's normalize().
export function normalizeEditValue(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.trim();
  return t === "" ? null : t;
}

// Build a minimal PATCH body: only the keys whose normalized draft value
// differs from the normalized original. The written value is the trimmed
// form (or null to clear) — never the raw untrimmed input, so we never
// persist accidental whitespace. Returns {} when nothing changed, which
// the caller treats as "no save needed".
export function computeEditPatch<K extends string>(
  original: Record<K, string>,
  draft: Record<K, string>,
): Record<string, string | null> {
  const patch: Record<string, string | null> = {};
  for (const key of Object.keys(draft) as K[]) {
    const before = normalizeEditValue(original[key]);
    const after = normalizeEditValue(draft[key]);
    if (before !== after) {
      patch[key] = after;
    }
  }
  return patch;
}

export function hasEdits<K extends string>(
  original: Record<K, string>,
  draft: Record<K, string>,
): boolean {
  return Object.keys(computeEditPatch(original, draft)).length > 0;
}
